// Stadium Trial — player boost catalogue (content + data-driven effects).
//
// Boosts are picked at the camp (bonus stages) and stay active for the WHOLE run.
// Each boost carries a typed `effects` object that the combat engine resolves into
// modifiers (see engine/pveEngine.ts → resolveCombatModifiers). Effects REALLY
// change the simulation — they are never just UI text.
//
// To add a boost: add an id to PveBoostId, an entry to PVE_BOOSTS, and (if it
// introduces a brand-new mechanic) a hook in resolveCombatModifiers + simulateCombat.

export type PveBoostId =
  | "shield"
  | "execute"
  | "heal"
  | "glass_cannon"
  | "last_stand"
  | "barrier"
  | "bloodlust"
  | "riposte"
  | "greed";

/** All effect fields a boost may declare. All optional — a boost sets only its own. */
export interface PveBoostEffects {
  // Flat multipliers (stack multiplicatively across boosts).
  damageMultiplier?: number;        // player outgoing damage ×
  defenseMultiplier?: number;       // player defense stat × (0 = lose all defense)
  maxHpMultiplier?: number;         // reserved — not consumed in V1 (flat maxHp)

  // Start-of-fight shield (absorbs before HP; resets each fight).
  startFightShieldPercent?: number; // % of max HP
  startFightShieldFlat?: number;    // flat shield points (added to the % one)

  // Bloodlust — damage bonus from missing HP, LOCKED at fight start.
  damagePerMissingHpStart?: number; // +factor × missingFraction (1.0 = 1:1, 10% HP → +90%)

  // Riposte — react to enemy crits.
  counterOnEnemyCrit?: boolean;     // when the enemy crits you, strike back immediately

  // Greed — trade survivability for loot.
  rewardMultiplier?: number;        // × on this run's XP & gold rewards

  // Execute — conditional bonus vs low-HP enemies.
  executeThresholdPercent?: number; // enemy HP% at/under which it applies
  executeDamageMultiplier?: number; // damage × when the enemy is under the threshold

  // Heal — proc after each PLAYER attack.
  healProcChance?: number;          // 0..1
  healMissingHpPercent?: number;    // % of MISSING hp restored on proc

  // Last stand — once-per-fight reactive shield when low.
  triggerHpThresholdPercent?: number; // player HP% under which it fires
  shieldPercent?: number;             // shield granted (% of max HP)
  oncePerFight?: boolean;
}

export interface PveBoostDef {
  id: PveBoostId;
  name: string;
  description: string;
  nameFr?: string;        // French (Stadium Trial i18n) — falls back to `name`
  descFr?: string;        // French — falls back to `description`
  nameKo?: string;        // Korean — falls back to `name`
  descKo?: string;        // Korean — falls back to `description`
  icon: string;   // emoji
  color: string;  // accent (text / border)
  bg: string;     // subtle tint (chip / selected background)
  effects: PveBoostEffects;
}

export const PVE_BOOSTS: Record<PveBoostId, PveBoostDef> = {
  shield: {
    id: "shield",
    name: "Shield",
    description: "Start each fight with a shield equal to 20% of your max HP, but deal 10% less damage.",
    nameFr: "Bouclier",
    descFr: "Commence chaque combat avec un bouclier égal à 20% de tes PV max, mais inflige 10% de dégâts en moins.",
    nameKo: "보호막",
    descKo: "각 전투를 최대 체력의 20%에 해당하는 보호막과 함께 시작하지만, 데미지가 10% 감소합니다.",
    icon: "🛡️",
    color: "#4CC9F0",
    bg: "rgba(76,201,240,0.12)",
    effects: { startFightShieldPercent: 20, damageMultiplier: 0.9 },
  },
  execute: {
    id: "execute",
    name: "Execute",
    description: "Deal +40% damage to enemies below 30% HP.",
    nameFr: "Exécution",
    descFr: "Inflige +40% de dégâts aux ennemis sous 30% PV.",
    nameKo: "처형",
    descKo: "체력이 30% 미만인 적에게 +40% 데미지를 입힙니다.",
    icon: "🗡️",
    color: "#FF3B47",
    bg: "rgba(255,59,71,0.12)",
    effects: { executeDamageMultiplier: 1.4, executeThresholdPercent: 30 },
  },
  heal: {
    id: "heal",
    name: "Heal",
    description: "10% chance after each of your attacks to recover 10% of your missing HP.",
    nameFr: "Soin",
    descFr: "10% de chance après chacune de tes attaques de récupérer 10% de tes PV manquants.",
    nameKo: "회복",
    descKo: "공격할 때마다 10% 확률로 잃은 체력의 10%를 회복합니다.",
    icon: "💚",
    color: "#2DE36B",
    bg: "rgba(45,227,107,0.12)",
    effects: { healProcChance: 0.1, healMissingHpPercent: 10 },
  },
  glass_cannon: {
    id: "glass_cannon",
    name: "Glass Cannon",
    description: "Lose all your Defense, but deal 20% more damage.",
    nameFr: "Canon de verre",
    descFr: "Perds toute ta Défense, mais inflige 20% de dégâts en plus.",
    nameKo: "유리 대포",
    descKo: "방어력을 모두 잃는 대신 데미지가 20% 증가합니다.",
    icon: "💥",
    color: "#C084FC",
    bg: "rgba(192,132,252,0.12)",
    effects: { defenseMultiplier: 0, damageMultiplier: 1.2 },
  },
  last_stand: {
    id: "last_stand",
    name: "Last Stand",
    description: "Once per fight, gain a shield equal to 25% max HP when you drop below 30% HP.",
    nameFr: "Baroud d'honneur",
    descFr: "Une fois par combat, gagne un bouclier égal à 25% des PV max quand tu passes sous 30% PV.",
    nameKo: "최후의 저항",
    descKo: "전투당 1회, 체력이 30% 미만으로 떨어지면 최대 체력의 25%에 해당하는 보호막을 얻습니다.",
    icon: "🔰",
    color: "#F5B301",
    bg: "rgba(245,179,1,0.12)",
    effects: { triggerHpThresholdPercent: 30, shieldPercent: 25, oncePerFight: true },
  },
  barrier: {
    id: "barrier",
    name: "Barrier",
    description: "Start each fight with a 150 HP shield (a blue bar that absorbs damage before your health).",
    nameFr: "Barrière",
    descFr: "Commence chaque combat avec un bouclier de 150 PV (une barre bleue qui absorbe les dégâts avant ta vie).",
    nameKo: "배리어",
    descKo: "각 전투를 150 체력 보호막과 함께 시작합니다 (체력보다 먼저 데미지를 흡수하는 파란 바).",
    icon: "🔵",
    color: "#4CC9F0",
    bg: "rgba(76,201,240,0.12)",
    effects: { startFightShieldFlat: 150 },
  },
  bloodlust: {
    id: "bloodlust",
    name: "Bloodlust",
    description: "Deal +1% damage for every 1% of max HP you are missing, locked in at the start of each fight.",
    nameFr: "Soif de sang",
    descFr: "Inflige +1% de dégâts par 1% de PV max manquants, figé au début de chaque combat.",
    nameKo: "피의 갈망",
    descKo: "잃은 최대 체력 1%마다 +1% 데미지를 입히며, 각 전투 시작 시 고정됩니다.",
    icon: "🩸",
    color: "#FF3B47",
    bg: "rgba(255,59,71,0.12)",
    effects: { damagePerMissingHpStart: 1.0 },
  },
  riposte: {
    id: "riposte",
    name: "Riposte",
    description: "When the enemy lands a critical hit on you, immediately strike back.",
    nameFr: "Riposte",
    descFr: "Quand l'ennemi te met un coup critique, contre-attaque immédiatement.",
    nameKo: "반격",
    descKo: "적이 치명타를 입히면 즉시 반격합니다.",
    icon: "↩️",
    color: "#F5B301",
    bg: "rgba(245,179,1,0.12)",
    effects: { counterOnEnemyCrit: true },
  },
  greed: {
    id: "greed",
    name: "Greed",
    description: "+15% run rewards (XP & gold), but -15% max HP.",
    nameFr: "Cupidité",
    descFr: "+15% de récompenses de run (XP & or), mais -15% de PV max.",
    nameKo: "탐욕",
    descKo: "런 보상(XP 및 골드) +15%, 대신 최대 체력 -15%.",
    icon: "💰",
    color: "#F5B301",
    bg: "rgba(245,179,1,0.12)",
    effects: { rewardMultiplier: 1.15, maxHpMultiplier: 0.85 },
  },
};

/** Full pool (draft draws a random subset from this, minus what you already own). */
export const BOOST_POOL: PveBoostId[] = ["shield", "execute", "heal", "glass_cannon", "last_stand", "barrier", "bloodlust", "riposte", "greed"];

/** How many boosts are offered at each choice (random draft). */
export const BOOST_DRAFT_SIZE = 3;

/** Safe lookup — returns undefined for unknown/legacy ids (e.g. old "physique"). */
export function getBoost(id: string): PveBoostDef | undefined {
  return (PVE_BOOSTS as Record<string, PveBoostDef>)[id];
}

/** True when the id maps to a real boost (used to drop legacy ids from saved runs). */
export function isValidBoost(id: string): id is PveBoostId {
  return getBoost(id) !== undefined;
}
